import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appActions } from '../../../store';
import LoadingScreen from '../../shared/LoadingScreen/LoadingScreen';
import ChooseSceneModal from '../../shared/ChooseSceneModal/ChooseSceneModal';
import styles from './Visualizer.module.css';
import zoomreset from './assets/zoomreset.svg';
import undo from './assets/undo.svg';
import redo from './assets/redo.svg';

export default function Visualizer() {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [showSceneModal, setShowSceneModal] = useState(false);
    const isUndoRedoAction = useRef(false);
    
    const activeProfile = useSelector(state => state.app.activeProfile);
    const productSelection = useSelector(state => state.app.productSelection);
    const roomscene = useSelector(state => state.app.roomscene);
    const style = useSelector(state => state.app.style);
    const size = useSelector(state => state.app.size);
    const currentView = useSelector(state => state.app.currentView);
    
    // Use Redux currentView as the source of truth
    const viewMode = currentView || 'swatch';

    // Determine image size based on zoom level
    const imageSize = zoom > 1 ? 3000 : 1500;

    // Build swatch view URL
    const getSwatchUrl = () => {
        if (!activeProfile || !activeProfile.description) return null;
        
        // Use decalID from description field
        const decalID = activeProfile.description;
        const selectionList = productSelection.length > 0 
            ? productSelection.join(',') 
            : '-1';
        
        return `https://renderfarm1.chameleonpower.com/cham/getImage.ashx?specifier=!${decalID}&sel=${selectionList}&size=${imageSize}&extra=(DESIGN=(MODEL=TILE),focus=(@size[x]/@res[x]/1in),size=(?,(@size[x]*@res[y]/@res[x])))`;
    };

    // Build room view URL
    const getRoomUrl = () => {
        if (!activeProfile || !roomscene) return null;
        
        const selectionList = productSelection.length > 0 
            ? productSelection.join(',') 
            : '-1';
        
        return `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?spec=${roomscene}&type=base&size=${imageSize}&sel=${activeProfile.id}(${selectionList})`;
    };

    const imageUrl = viewMode === 'swatch' ? getSwatchUrl() : getRoomUrl();

    // Update Redux with current image URL
    useEffect(() => {
        if (imageUrl) {
            dispatch(appActions.setCurrentImageUrl(imageUrl));
        }
    }, [imageUrl, dispatch]);

    // Show loading when imageUrl changes
    useEffect(() => {
        if (imageUrl) {
            setIsLoading(true);
        }
    }, [imageUrl]);

    // Track history when style, size, or productSelection changes
    useEffect(() => {
        // Skip if this is an undo/redo action
        if (isUndoRedoAction.current) {
            isUndoRedoAction.current = false;
            return;
        }

        // Skip if no style or size (initial load)
        if (!style || !size) return;

        const currentState = {
            style,
            size,
            productSelection: [...productSelection]
        };

        // Check if this state is different from the last history item
        const lastState = history[historyIndex];
        if (lastState && 
            lastState.style === currentState.style && 
            lastState.size === currentState.size &&
            JSON.stringify(lastState.productSelection) === JSON.stringify(currentState.productSelection)) {
            return; // No change, don't add to history
        }

        // Remove any future history if we're not at the end
        const newHistory = history.slice(0, historyIndex + 1);
        
        // Add new state to history
        newHistory.push(currentState);
        
        // Limit history to 50 items
        if (newHistory.length > 50) {
            newHistory.shift();
            setHistory(newHistory);
            setHistoryIndex(newHistory.length - 1);
        } else {
            setHistory(newHistory);
            setHistoryIndex(newHistory.length - 1);
        }
    }, [style, size, productSelection]);

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    const handleImageError = () => {
        setIsLoading(false);
    };

    const handleViewModeChange = (mode) => {
        if (mode !== viewMode) {
            setIsLoading(true);
            dispatch(appActions.setCurrentView(mode));
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 0.25, 3)); // Max zoom 3x
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 0.25, 0.5)); // Min zoom 0.5x
    };

    const handleZoomReset = () => {
        setZoom(1); // Reset to 100%
        setPanPosition({ x: 0, y: 0 }); // Reset pan position
    };

    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({ 
                x: e.clientX - panPosition.x, 
                y: e.clientY - panPosition.y 
            });
            e.preventDefault();
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging || zoom <= 1) return;

        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        
        // Increase pan range based on zoom level - allow full corner access
        const maxPan = (zoom - 1) * 500;
        
        setPanPosition({
            x: Math.max(-maxPan, Math.min(maxPan, newX)),
            y: Math.max(-maxPan, Math.min(maxPan, newY))
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleUndo = () => {
        if (historyIndex > 0) {
            const previousState = history[historyIndex - 1];
            isUndoRedoAction.current = true;
            
            // Apply previous state to Redux
            dispatch(appActions.setStyle(previousState.style));
            dispatch(appActions.setSize(previousState.size));
            dispatch(appActions.setProductSelection(previousState.productSelection));
            
            setHistoryIndex(historyIndex - 1);
        }
    };

    const handleRedo = () => {
        if (historyIndex < history.length - 1) {
            const nextState = history[historyIndex + 1];
            isUndoRedoAction.current = true;
            
            // Apply next state to Redux
            dispatch(appActions.setStyle(nextState.style));
            dispatch(appActions.setSize(nextState.size));
            dispatch(appActions.setProductSelection(nextState.productSelection));
            
            setHistoryIndex(historyIndex + 1);
        }
    };

    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < history.length - 1;

    // Check if all color slots are filled
    const allSlotsFilled = () => {
        if (!activeProfile || !activeProfile.descriptor) return false;
        
        const slots = activeProfile.descriptor.split(',').map(s => s.trim());
        const requiredSlots = slots.length;
        
        // Check if we have selections for all slots and none are -1
        if (productSelection.length < requiredSlots) return false;
        
        for (let i = 0; i < requiredSlots; i++) {
            if (!productSelection[i] || productSelection[i] === -1) {
                return false;
            }
        }
        
        return true;
    };

    const showToggle = allSlotsFilled();

    // Check if current room scene is a user-uploaded image
    const isUserUploadedScene = roomscene && roomscene.includes('_USER_');

    const handleSceneSelect = (specifier) => {
        dispatch(appActions.setRoomscene(specifier));
        setShowSceneModal(false);
    };

    const handleEditImage = () => {
        // Save current URL before navigating
        localStorage.setItem('current_selection', window.location.href);
        // Navigate to image editor with current room scene specifier
        window.location.href = `/im.aspx?spec=${encodeURIComponent(roomscene)}`;
    };

    return (
        <div className={styles.visualizer}>
            <LoadingScreen isOpen={isLoading} />
            <ChooseSceneModal 
                isOpen={showSceneModal} 
                onClose={() => setShowSceneModal(false)}
                onSceneSelect={handleSceneSelect}
            />

            {/* Change Room Button - Only visible in room view and when all slots filled */}
            {viewMode === 'room' && showToggle && (
                <div className={styles.roomButtonsContainer}>
                    {isUserUploadedScene && (
                        <button 
                            className={styles.editImageButton}
                            onClick={handleEditImage}
                            aria-label="Edit image"
                        >
                            Edit Image
                        </button>
                    )}
                    <button 
                        className={styles.changeRoomButton}
                        onClick={() => setShowSceneModal(true)}
                        aria-label="Change room"
                    >
                        Change Room
                    </button>
                </div>
            )}

            {/* Toggle Button - Only visible when all slots filled */}
            {showToggle && (
                <div className={styles.toggleContainer}>
                    <button 
                        className={`${styles.toggleOption} ${viewMode === 'swatch' ? styles.active : ''}`}
                        onClick={() => handleViewModeChange('swatch')}
                        aria-label="Switch to swatch view"
                    >
                        Swatch
                    </button>
                    <button 
                        className={`${styles.toggleOption} ${viewMode === 'room' ? styles.active : ''}`}
                        onClick={() => handleViewModeChange('room')}
                        aria-label="Switch to room view"
                    >
                        Room
                    </button>
                </div>
            )}

            {/* Image Display */}
            <div 
                className={styles.imageContainer}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                {imageUrl ? (
                    <img 
                        src={imageUrl} 
                        alt={viewMode === 'swatch' ? 'Product swatch' : 'Room view'}
                        className={styles.visualizerImage}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        draggable={false}
                        style={{ 
                            display: isLoading ? 'none' : 'block',
                            transform: `scale(${zoom}) translate(${panPosition.x / zoom}px, ${panPosition.y / zoom}px)`,
                            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                        }}
                    />
                ) : (
                    <div className={styles.placeholder}>
                        <p>Select a style and size to view</p>
                    </div>
                )}
            </div>

            {/* Control Buttons */}
            <div className={styles.controlButtons}>
                <button 
                    className={styles.controlButton}
                    onClick={handleZoomIn}
                    aria-label="Zoom in"
                    title="Zoom in"
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="8" y1="5" x2="8" y2="11" stroke="currentColor" strokeWidth="2"/>
                        <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="2"/>
                        <line x1="13" y1="13" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                </button>
                <button 
                    className={styles.controlButton}
                    onClick={handleZoomOut}
                    aria-label="Zoom out"
                    title="Zoom out"
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="2"/>
                        <line x1="13" y1="13" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                </button>
                <button 
                    className={`${styles.controlButton} ${zoom === 1 ? styles.disabled : ''}`}
                    onClick={handleZoomReset}
                    disabled={zoom === 1}
                    aria-label="Reset zoom"
                    title="Reset zoom"
                >
                    {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                        <text x="8" y="11" fontSize="8" textAnchor="middle" fill="currentColor" fontWeight="bold">1:1</text>
                        <line x1="13" y1="13" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                    </svg> */}
                    <img src={zoomreset} width="24" height="24" alt="zoom reset"/>
                </button>
                <button 
                    className={`${styles.controlButton} ${!canUndo ? styles.disabled : ''}`}
                    onClick={handleUndo}
                    disabled={!canUndo}
                    aria-label="Undo"
                    title="Undo"
                >
                    {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M5 10 L10 5 L10 8 C14 8 16 10 16 14 C16 15 15.5 16 15 16.5 C14 14 12 13 10 13 L10 15 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
   
                    </svg> */}
                    <img src={undo} width="24" height="24" alt="undo"/>

                </button>
                <button 
                    className={`${styles.controlButton} ${!canRedo ? styles.disabled : ''}`}
                    onClick={handleRedo}
                    disabled={!canRedo}
                    aria-label="Redo"
                    title="Redo"
                >
                    {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M15 10 L10 5 L10 8 C6 8 4 10 4 14 C4 15 4.5 16 5 16.5 C6 14 8 13 10 13 L10 15 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    </svg> */}
                     <img src={redo} width="24" height="24" alt="redo"/>
                </button>
            </div>
        </div>
    );
}
